import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Smartphone,
  Download,
  Link2,
  Clock,
  Wifi,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  Sparkles,
  Share2,
  AlertCircle,
  ShieldCheck,
  CheckCircle2,
  Zap,
  Globe,
  MessageCircle,
  RefreshCw,
  Sliders,
  ArrowRight,
} from 'lucide-react';
import { CoupleSettings, AppDownloadLink, DownloadLinkDuration } from '../../types';
import { getClientAppVersion } from '../../utils/appVersion';
import {
  DURATION_OPTIONS,
  getDurationLabel,
  calculateExpirationDate,
  isDownloadLinkExpired,
  getRemainingTimeLabel,
  generateSecureToken,
  buildDirectDownloadUrl,
} from '../../utils/appDownloadUtils';
import { usePwaInstall } from '../../utils/usePwaInstall';
import { recordAppDownloadInDb } from '../../services/firebaseDb';
import { romanticAudio } from '../../utils/audio';

interface AppAdminSectionProps {
  settings: CoupleSettings;
  activeProfile: 'novio' | 'novia';
  onSaveSettings: (newSettings: CoupleSettings) => void;
  onNavigateToPublic: (route?: string) => void;
}

export const AppAdminSection: React.FC<AppAdminSectionProps> = ({
  settings,
  activeProfile,
  onSaveSettings,
  onNavigateToPublic,
}) => {
  const { triggerInstall } = usePwaInstall();

  // Estado para nuevo enlace
  const [selectedDuration, setSelectedDuration] = useState<DownloadLinkDuration>('1_day');
  const [linkLabel, setLinkLabel] = useState<string>('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [latestGeneratedLink, setLatestGeneratedLink] = useState<AppDownloadLink | null>(null);
  const [installPromptResult, setInstallPromptResult] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [currentClientVersion, setCurrentClientVersion] = useState<string>(() => getClientAppVersion());

  // Actualizar versión del cliente localmente si cambia
  React.useEffect(() => {
    setCurrentClientVersion(getClientAppVersion());
  }, []);

  // Asegurar que la app esté 100% habilitada y desbloqueada
  React.useEffect(() => {
    if (settings.appKillSwitch?.isEnabled) {
      onSaveSettings({
        ...settings,
        appKillSwitch: {
          ...settings.appKillSwitch,
          isEnabled: false,
        },
      });
      try {
        const bc = new BroadcastChannel('pareja_security_channel');
        bc.postMessage({
          type: 'APP_ENABLED',
          killSwitch: { ...settings.appKillSwitch, isEnabled: false },
        });
        bc.close();
      } catch {}
    }
  }, [settings.appKillSwitch?.isEnabled]);

  const downloadLinks: AppDownloadLink[] = settings.appDownloadLinks || [];

  // Generar nuevo link
  const handleGenerateLink = () => {
    romanticAudio.playChime();
    const token = generateSecureToken();
    const expiresAt = calculateExpirationDate(selectedDuration);

    const newLink: AppDownloadLink = {
      id: token,
      token: token,
      label: linkLabel.trim() || `Enlace (${getDurationLabel(selectedDuration)})`,
      duration: selectedDuration,
      createdAt: new Date().toISOString(),
      expiresAt: expiresAt,
      downloadsCount: 0,
      createdBy: activeProfile,
      isActive: true,
    };

    const updatedLinks = [newLink, ...downloadLinks];
    onSaveSettings({
      ...settings,
      appDownloadLinks: updatedLinks,
    });

    setLatestGeneratedLink(newLink);
    setLinkLabel('');
    romanticAudio.playBloomSuccess();
  };

  // Copiar link al portapapeles
  const handleCopyLink = async (token: string) => {
    const url = buildDirectDownloadUrl(token);
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
      setCopiedToken(token);
      romanticAudio.playChime();
      setTimeout(() => setCopiedToken(null), 3000);
    } catch {
      // Fallback
    }
  };

  // Compartir por WhatsApp
  const handleShareWhatsApp = (token: string, label?: string) => {
    const url = buildDirectDownloadUrl(token);
    const partnerName = activeProfile === 'novio'
      ? (settings.partner2Name || 'mi amor')
      : (settings.partner1Name || 'mi amor');
    const text = encodeURIComponent(
      `¡Hola ${partnerName}! ❤️ Te comparto el enlace directo para descargar e instalar nuestra app oficial de recuerdos: ${url}`
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  };

  // Eliminar / Revocar link
  const handleDeleteLink = (id: string) => {
    const updatedLinks = downloadLinks.filter((l) => l.id !== id);
    onSaveSettings({
      ...settings,
      appDownloadLinks: updatedLinks,
    });
    if (latestGeneratedLink?.id === id) {
      setLatestGeneratedLink(null);
    }
    romanticAudio.playChime();
  };

  // Descargar app directamente en el dispositivo actual con un solo clic
  const handleInstallOnCurrentDevice = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    romanticAudio.playChime();

    // 1. Incrementar contador en base de datos
    try {
      await recordAppDownloadInDb();
    } catch {
      // Continuar si hay problemas de red
    }

    // 2. Incrementar conteo en configuración local
    onSaveSettings({
      ...settings,
      totalAppDownloads: (settings.totalAppDownloads || 0) + 1,
    });

    setInstallPromptResult('¡Iniciando descarga e instalación directa!');
    romanticAudio.playBloomSuccess();

    // 3. Si se encuentra dentro de un iframe (como el preview de AI Studio en móvil),
    // abrir de inmediato en Google Chrome nativo para permitir la instalación en 1 toque
    const isInsideIframe = typeof window !== 'undefined' && window.self !== window.top;
    if (isInsideIframe) {
      const directUrl = window.location.origin + '/?source=pwa&autoInstall=1';
      window.open(directUrl, '_blank');
      setIsDownloading(false);
      return;
    }

    // 4. Disparar prompt nativo directo de PWA
    try {
      await triggerInstall();
    } finally {
      setIsDownloading(false);
    }
  };

  // Descargar archivo de acceso directo Web para PC/Mac
  const handleDownloadShortcutFile = () => {
    romanticAudio.playChime();
    const appUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nuestrosrecuerdos.app';
    const content = `[InternetShortcut]\nURL=${appUrl}/\nIconIndex=0\nIconFile=${appUrl}/app-icon.svg`;
    const blob = new Blob([content], { type: 'application/internet-shortcut' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Nuestros Recuerdos App.url';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Contadores
  const activeLinksCount = downloadLinks.filter((l) => !isDownloadLinkExpired(l)).length;
  const totalDownloads = (settings.totalAppDownloads || 0) + downloadLinks.reduce((acc, l) => acc + (l.downloadsCount || 0), 0);

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-8">
      {/* Encabezado principal */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shrink-0">
            <Smartphone className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 font-serif tracking-tight">
              App Móvil & Descargas
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Descarga la app de la web y genera enlaces de descarga directos con expiración.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
            <Wifi className="w-3.5 h-3.5" />
            <span>100% Online en Tiempo Real</span>
          </span>
        </div>
      </div>

      {/* SECCIÓN PRINCIPAL: DESCARGAR LA APP DE LA WEB */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 shadow-xs relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-rose-200 p-1.5 shadow-xs shrink-0 flex items-center justify-center">
              <img src="/app-icon.svg" alt="App Icon" className="w-full h-full object-contain rounded-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 font-serif">
                  Descargar la App de la Web
                </h3>
                <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase tracking-wider">
                  PWA Standalone
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-xl leading-relaxed">
                Instala la aplicación web en tu dispositivo. La app se ejecuta a pantalla completa, sin barras de navegador, con icono personalizado en tu pantalla de inicio y sincronización inmediata.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-rose-700 font-medium">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-rose-500" />
                  Solo Online (No funciona offline)
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                  Sincronización en vivo
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            {/* Total de Descargas */}
            <div className="px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2.5 justify-center">
              <Download className="w-4 h-4 text-rose-500" />
              <div className="text-left">
                <span className="text-[10px] uppercase font-bold text-slate-400 block leading-none">
                  Descargas Totales
                </span>
                <span className="text-base font-black text-slate-900 leading-tight">
                  {totalDownloads.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Botón directo único de Descargar App */}
            <button
              type="button"
              id="btn-instalar-app-admin"
              onClick={handleInstallOnCurrentDevice}
              disabled={isDownloading}
              className="py-3 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm shadow-xs flex items-center justify-center gap-2 active:scale-98 transition-all cursor-pointer disabled:opacity-75"
            >
              <Download className={`w-4 h-4 text-rose-400 ${isDownloading ? 'animate-bounce' : ''}`} />
              <span>{isDownloading ? 'Descargando...' : 'Descargar App'}</span>
            </button>
          </div>
        </div>

        {/* Mensaje de confirmación inmediata */}
        {installPromptResult && (
          <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{installPromptResult}</span>
          </div>
        )}
      </div>

      {/* SECCIÓN 2: GENERADOR DE LINKS DE DESCARGA DIRECTOS */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shrink-0">
            <Link2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 font-serif">
              Generar Link de Descarga Directo
            </h3>
            <p className="text-xs text-slate-500">
              Crea un enlace único para compartir la app y define cuánto tiempo estará disponible.
            </p>
          </div>
        </div>

        {/* Formulario */}
        <div className="space-y-5">
          {/* Selector de Duración */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5">
              Establecer Duración del Enlace:
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {DURATION_OPTIONS.map((opt) => {
                const isSelected = selectedDuration === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setSelectedDuration(opt.id);
                      romanticAudio.playChime();
                    }}
                    className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-rose-50 border-rose-500 text-rose-950 shadow-xs'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-xs sm:text-sm">{opt.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-rose-600" />}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      {opt.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Etiqueta / Nombre del destinatario */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Etiqueta o Destinatario (Opcional):
            </label>
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder={`Ej: Para ${activeProfile === 'novio' ? settings.partner2Name || 'Pareja' : settings.partner1Name || 'Pareja'} o Descarga Móvil`}
              className="w-full py-2.5 px-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-400 transition-all"
            />
          </div>

          {/* Botón de Generación */}
          <button
            type="button"
            id="btn-generar-link-directo"
            onClick={handleGenerateLink}
            className="w-full sm:w-auto py-3 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm shadow-xs flex items-center justify-center gap-2 active:scale-98 transition-all cursor-pointer"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Generar Link de Descarga Directo</span>
          </button>
        </div>

        {/* Tarjeta del último link generado */}
        {latestGeneratedLink && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-5 rounded-2xl bg-rose-50/50 border border-rose-200 text-left"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-900">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ¡Enlace generado exitosamente!
              </span>
              <span className="text-xs text-amber-700 font-semibold">
                {getRemainingTimeLabel(latestGeneratedLink.expiresAt).text}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="flex-1 py-2 px-3.5 rounded-xl bg-white border border-slate-200 text-xs font-mono text-slate-800 truncate select-all">
                {buildDirectDownloadUrl(latestGeneratedLink.token)}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopyLink(latestGeneratedLink.token)}
                  className="flex-1 sm:flex-initial py-2 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  {copiedToken === latestGeneratedLink.token ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-white" />
                      <span>¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copiar Link</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleShareWhatsApp(latestGeneratedLink.token, latestGeneratedLink.label)}
                  title="Compartir por WhatsApp"
                  className="py-2 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs flex items-center justify-center transition-all cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => onNavigateToPublic(`/descargar-app?token=${latestGeneratedLink.token}`)}
                  title="Abrir enlace de descarga"
                  className="py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs flex items-center justify-center transition-all cursor-pointer"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* SECCIÓN 3: LISTADO DE ENLACES ACTIVOS E HISTORIAL */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 font-serif">
                Enlaces de Descarga Creados
              </h3>
              <p className="text-xs text-slate-500">
                Historial de links generados con su estado y contador de descargas.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <span className="px-3 py-1 rounded-xl bg-slate-100 border border-slate-200 text-slate-700">
              Activos: <strong className="text-slate-900">{activeLinksCount}</strong>
            </span>
            <span className="px-3 py-1 rounded-xl bg-rose-50 text-rose-700 border border-rose-200">
              Descargas totales: <strong className="text-rose-900">{totalDownloads}</strong>
            </span>
          </div>
        </div>

        {downloadLinks.length === 0 ? (
          <div className="text-center py-12 px-4 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-slate-500">
            <Link2 className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p className="text-sm font-semibold text-slate-700">No hay enlaces de descarga creados todavía</p>
            <p className="text-xs text-slate-500 mt-1">
              Usa el generador superior para crear tu primer link directo con duración.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {downloadLinks.map((link) => {
              const expired = isDownloadLinkExpired(link);
              const time = getRemainingTimeLabel(link.expiresAt);

              return (
                <div
                  key={link.id}
                  className={`p-4 rounded-xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                    expired
                      ? 'bg-slate-50/70 border-slate-200 opacity-60'
                      : 'bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                  }`}
                >
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-sm text-slate-900">
                        {link.label || 'Enlace Directo'}
                      </span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                          expired
                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                            : time.isPermanent
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-amber-50 text-amber-800 border border-amber-200'
                        }`}
                      >
                        {time.text}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        ({getDurationLabel(link.duration)})
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>Token: <code className="text-slate-700 font-mono">{link.token.slice(0, 10)}...</code></span>
                      <span>&bull;</span>
                      <span>Descargas: <strong className="text-rose-600 font-bold">{link.downloadsCount || 0}</strong></span>
                      <span>&bull;</span>
                      <span>Creado por: <strong className="capitalize text-slate-700">{link.createdBy || 'Admin'}</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                    <button
                      type="button"
                      onClick={() => handleCopyLink(link.token)}
                      disabled={expired}
                      className="py-1.5 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-xs text-slate-700 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                    >
                      {copiedToken === link.token ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span>¡Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copiar</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleShareWhatsApp(link.token, link.label)}
                      disabled={expired}
                      title="Compartir por WhatsApp"
                      className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs flex items-center justify-center transition-all cursor-pointer disabled:opacity-40"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>

                    <button
                      type="button"
                      onClick={() => onNavigateToPublic(`/descargar-app?token=${link.token}`)}
                      title="Abrir enlace"
                      className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs flex items-center justify-center transition-all cursor-pointer"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteLink(link.id)}
                      title="Eliminar / Revocar enlace"
                      className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-xs flex items-center justify-center transition-all cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECCIÓN 4: REQUISITO TÉCNICO Y POLÍTICA ONLINE EN TIEMPO REAL */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200/80 p-5 sm:p-6 text-left text-xs text-slate-600 space-y-1.5">
        <div className="flex items-center gap-2 text-slate-800 font-bold text-xs sm:text-sm">
          <Wifi className="w-4 h-4 text-rose-500" />
          <span>Arquitectura: Funcionamiento 100% Online en Tiempo Real</span>
        </div>
        <p className="leading-relaxed text-slate-500">
          La app está configurada para que <strong>nunca funcione en modo offline</strong>. Todos los datos, álbum de fotos, cartas de amor, riego de tulipanes y música de fondo se consultan en vivo directamente desde Firestore. Si el usuario pierde conexión a internet, se despliega una pantalla de bloqueo que solicita reconexión inmediata para evitar datos desactualizados.
        </p>
      </div>
    </div>
  );
};
